/**
 * OperationsCenterPage.jsx — Tax Flow Operations Center 2.0 (Refinement)
 * Preview refinement: compact cards, client name primary, sort/filter, aging labels,
 * department visibility, context-appropriate action affordances.
 *
 * Routes: /, /live-ops, /operations-center
 * Six tabs: Open Engagements (Kanban) · Estimates (Kanban) ·
 *           Unanswered Messages · Waiting on Staff · Waiting on Client · Meetings
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Briefcase, FileText, MessageSquare, Clock, Video,
  Search, RefreshCw, Loader2, User, Users,
  CalendarClock, CheckSquare, Globe, BookOpen, CreditCard,
  Zap, Activity, ChevronDown, ArrowUpDown,
  ExternalLink,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import OpsWorkflowNav from "@/components/workflow/OpsWorkflowNav";

// ─── Brand palette ────────────────────────────────────────────────────────────
const BRAND_BG    = "#F2F5F9";
const BRAND_TEXT  = "#21364F";
const BRAND_MUTED = "#64748B";
const BRAND_NAVY  = "#172B46";

// ─── Tab definitions ──────────────────────────────────────────────────────────
const TABS = [
  { key: "engagements",    label: "Open Engagements",       icon: Briefcase,     color: "#2563EB" },
  { key: "estimates",      label: "Estimates & Follow-ups",  icon: FileText,      color: "#7C3AED" },
  { key: "messages",       label: "Unanswered Messages",     icon: MessageSquare, color: "#0891B2" },
  { key: "waiting_staff",  label: "Waiting on Staff",        icon: Users,         color: "#D97706" },
  { key: "waiting_client", label: "Waiting on Client",       icon: Clock,         color: "#0D9488" },
  { key: "meetings",       label: "Meetings to Review",      icon: Video,         color: "#4F46E5" },
];

// ─── Kanban stages ────────────────────────────────────────────────────────────
const ENG_STAGES = [
  { key: "engagement_signed", label: "Engagement Signed", color: "#64748B" },
  { key: "organizer",         label: "Organizer",         color: "#D99B20" },
  { key: "documents",         label: "Documents",         color: "#2563EB" },
  { key: "production",        label: "Production",        color: "#8B5CF6" },
  { key: "review_filing",     label: "Review & Filing",   color: "#059669" },
];
const EST_STAGES = [
  { key: "estimate_sent",     label: "Estimate Sent",     color: "#64748B" },
  { key: "follow_up_active",  label: "Follow-up Active",  color: "#D99B20" },
  { key: "staff_attention",   label: "Staff Attention",   color: "#DC3545" },
  { key: "awaiting_decision", label: "Awaiting Decision", color: "#2563EB" },
];

const MGMT_ROLES = new Set(["administrator", "partner", "manager"]);

// ─── Source-type → icon ───────────────────────────────────────────────────────
const SRC_ICON = {
  unanswered_message:  MessageSquare,
  webchat_abandoned:   MessageSquare,
  appointment_request: CalendarClock,
  zoom_review:         Video,
  follow_up_due:       CalendarClock,
  lifecycle_item:      Briefcase,
  outstanding_invoice: CreditCard,
  production_ready:    Briefcase,
  pending_activation:  Zap,
  fbar_ready:          Globe,
  str_active:          BookOpen,
  assigned_task:       CheckSquare,
  portal_activated:    Activity,
};

// ─── Action label per queue tab ───────────────────────────────────────────────
const TAB_ACTION = {
  messages:       "Open Conversation",
  waiting_staff:  "Open Task",
  waiting_client: "View Requirement",
  meetings:       "Review Meeting",
};
const SRC_ACTION_OVERRIDE = {
  unanswered_message:  "Open Conversation",
  webchat_abandoned:   "Open Conversation",
  appointment_request: "View Appointment",
  zoom_review:         "Review Meeting",
  outstanding_invoice: "View Invoice",
  portal_activated:    "View Portal",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function daysSince(dateStr) {
  if (!dateStr) return null;
  try {
    const diff = Math.floor((Date.now() - new Date(dateStr)) / 86400000);
    return diff >= 0 ? diff : null;
  } catch { return null; }
}
function formatAge(days) {
  if (days === null || days === undefined) return null;
  if (days === 0) return "today";
  if (days === 1) return "1 day old";
  return `${days} days old`;
}
function formatDate(dateStr) {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch { return null; }
}
function isPast(dateStr) {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date();
}

function matchSearch(item, q) {
  if (!q) return true;
  const lq = q.toLowerCase();
  return [
    item.client_name, item.task_title, item.team_label, item.assigned_to,
    item.title, item.engagement_type, item.engagement_number, item.proposal_number,
    item.services_summary,
  ].some(v => v && String(v).toLowerCase().includes(lq));
}

// ─── Item normalizer ──────────────────────────────────────────────────────────
/**
 * Maps any ops-summary source item to a common NormalizedItem structure.
 * Never invents client names — uses "Internal / Administrative Task" for internal items
 * and "Client Not Linked" where a client relationship cannot be resolved.
 */
function normalizeItem(item, sourceType, tabKey) {
  const defaultActionLabel = SRC_ACTION_OVERRIDE[sourceType] || TAB_ACTION[tabKey] || "Open Task";

  switch (sourceType) {
    case "lifecycle_item":
      return {
        uid:         `lc-${item.client_id}-${item.obligation_code || ""}`,
        client_name: validName(item.client_name) || "Client Not Linked",
        task_title:  item.next_action_label || "Action Required",
        context:     item.scope_label || item.current_state_label,
        link:        item.destination,
        client_id:   item.client_id,
        assigned_to: null,
        team_label:  null,
        created_at:  null,
        due_at:      null,
        is_overdue:  false,
        source_type: sourceType,
        action_label: defaultActionLabel,
      };

    case "portal_activated":
      return {
        uid:         `portal-${item.client_id}`,
        client_name: validName(item.client_name) || "Client Not Linked",
        task_title:  `Portal Activated — Review ${item.lifecycle_type || "Record"}`,
        context:     null,
        link:        item.link,
        client_id:   item.client_id,
        assigned_to: null,
        team_label:  item.team_label || null,
        created_at:  item.activated_at,
        due_at:      null,
        is_overdue:  false,
        source_type: sourceType,
        action_label: defaultActionLabel,
      };

    case "assigned_task": {
      const overdue = item.due_at ? isPast(item.due_at) : false;
      return {
        uid:         item.id,
        client_name: validName(item.client_name) || "Internal / Administrative Task",
        task_title:  item.title || "Task",
        context:     item.priority ? `Priority: ${item.priority}` : null,
        link:        item.link,
        client_id:   item.client_id,
        assigned_to: item.assigned_to_name,
        team_label:  null,
        created_at:  item.created_at,
        due_at:      item.due_at,
        is_overdue:  overdue,
        priority:    item.priority,
        source_type: sourceType,
        action_label: defaultActionLabel,
      };
    }

    case "follow_up_due": {
      const overdue = item.follow_up_at ? isPast(item.follow_up_at) : false;
      return {
        uid:         item.id,
        client_name: validName(item.client_name) || "Client Not Linked",
        task_title:  item.subtitle || item.reason_label || "Follow-Up Due",
        context:     item.note,
        link:        item.link,
        client_id:   item.client_id,
        assigned_to: item.assigned_to_name,
        team_label:  null,
        created_at:  item.deferred_at,
        due_at:      item.follow_up_at,
        is_overdue:  overdue,
        source_type: sourceType,
        action_label: defaultActionLabel,
      };
    }

    case "str_active":
      return {
        uid:         item.id,
        client_name: validName(item.client_name) || "Client Not Linked",
        task_title:  item.str_next_action_label || item.str_state_label || "Strategic Review",
        context:     item.scope_label || item.engagement_number,
        link:        item.str_workspace_target || (item.client_id ? `/client-workspace/${item.client_id}` : null),
        client_id:   item.client_id,
        assigned_to: item.assigned_to_name,
        team_label:  item.assigned_team_name || null,
        created_at:  item.created_at,
        due_at:      null,
        is_overdue:  false,
        source_type: sourceType,
        action_label: defaultActionLabel,
      };

    case "production_ready":
      return {
        uid:         item.id,
        client_name: validName(item.client_name) || "Client Not Linked",
        task_title:  item.str_next_action_label || `Begin Production — ${item.service_name || "Engagement"}`,
        context:     item.scope_label,
        link:        item.str_workspace_target || item.destination || (item.client_id ? `/client-workspace/${item.client_id}` : null),
        client_id:   item.client_id,
        assigned_to: null,
        team_label:  null,
        created_at:  item.created_at,
        due_at:      null,
        is_overdue:  false,
        source_type: sourceType,
        action_label: defaultActionLabel,
      };

    case "pending_activation":
      return {
        uid:         item.engagement_id,
        client_name: validName(item.client_name) || "Client Not Linked",
        task_title:  `Activate — ${item.service_name || "Engagement"}`,
        context:     item.engagement_number,
        link:        item.client_id ? `/client-workspace/${item.client_id}` : null,
        client_id:   item.client_id,
        assigned_to: null,
        team_label:  null,
        created_at:  item.signed_at,
        due_at:      null,
        is_overdue:  false,
        source_type: sourceType,
        action_label: defaultActionLabel,
      };

    case "fbar_ready":
      return {
        uid:         item.id || `${item.client_id}-fbar`,
        client_name: validName(item.client_name) || "Client Not Linked",
        task_title:  "FBAR Preparation Ready",
        context:     item.engagement_number || item.filing_year,
        link:        item.destination || (item.client_id ? `/client-workspace/${item.client_id}` : null),
        client_id:   item.client_id,
        assigned_to: null,
        team_label:  null,
        created_at:  item.created_at,
        due_at:      null,
        is_overdue:  false,
        source_type: sourceType,
        action_label: defaultActionLabel,
      };

    case "unanswered_message":
    case "webchat_abandoned":
    case "appointment_request": {
      // title IS the client/contact name for message items
      const isInternal = !item.title && !item.client_id;
      return {
        uid:         item.id,
        client_name: item.title || (isInternal ? "Internal / Administrative Task" : "Client Not Linked"),
        task_title:  item.subtitle || (sourceType === "webchat_abandoned" ? "Unanswered Web Chat"
                       : sourceType === "appointment_request" ? "Appointment Request"
                       : "Unanswered Message"),
        context:     item.channel ? capitalise(item.channel.replace(/_/g, " ")) : null,
        link:        item.link,
        client_id:   item.client_id,
        assigned_to: item.assigned_to_name,
        team_label:  null,
        created_at:  item.detected_at || null,
        due_at:      null,
        is_overdue:  false,
        source_type: sourceType,
        action_label: defaultActionLabel,
      };
    }

    case "outstanding_invoice": {
      const overdue = item.is_overdue || (item.due_date ? isPast(item.due_date) : false);
      return {
        uid:         item.invoice_id,
        client_name: validName(item.client_name) || "Client Not Linked",
        // Do NOT expose balance/amount — finance-gated; show invoice number only
        task_title:  `Invoice ${item.invoice_number || ""} — Outstanding`,
        context:     item.status ? capitalise(item.status) : null,
        link:        item.client_id ? `/client-workspace/${item.client_id}` : null,
        client_id:   item.client_id,
        assigned_to: null,
        team_label:  null,
        created_at:  item.issue_date,
        due_at:      item.due_date,
        is_overdue:  overdue,
        source_type: sourceType,
        action_label: defaultActionLabel,
      };
    }

    // Zoom / meeting
    default: /* zoom_review and any unknown type */
      return {
        uid:         item.id || `${item.client_id}-${sourceType}`,
        client_name: validName(item.client_name) || validName(item.prospect_name) || "Client Not Linked",
        task_title:  item.title || item.subtitle || "Meeting",
        context:     item.subtitle,
        link:        item.link || (item.client_id ? `/case-analysis/${item.client_id}` : null),
        client_id:   item.client_id,
        assigned_to: item.assigned_to_name,
        team_label:  null,
        created_at:  item.detected_at,
        due_at:      null,
        is_overdue:  false,
        source_type: sourceType,
        action_label: defaultActionLabel,
      };
  }
}

function validName(v) {
  if (!v || v === "Unknown" || v === "Client") return null;
  return v;
}
function capitalise(str) {
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : str;
}

// ─── Sort / filter logic ──────────────────────────────────────────────────────
const SORT_OPTIONS = [
  { key: "oldest_first",   label: "Oldest first" },
  { key: "newest_first",   label: "Newest first" },
  { key: "overdue_first",  label: "Overdue / urgent first" },
  { key: "due_date",       label: "Due date" },
  { key: "assigned_staff", label: "Assigned staff" },
];

function sortItems(items, sortKey) {
  const arr = [...items];
  switch (sortKey) {
    case "overdue_first":
      return arr.sort((a, b) => {
        if (a.is_overdue !== b.is_overdue) return a.is_overdue ? -1 : 1;
        const da = a.created_at ? new Date(a.created_at) : new Date(0);
        const db_ = b.created_at ? new Date(b.created_at) : new Date(0);
        return da - db_; // then oldest
      });
    case "newest_first":
      return arr.sort((a, b) => {
        const da = a.created_at ? new Date(a.created_at) : new Date(0);
        const db_ = b.created_at ? new Date(b.created_at) : new Date(0);
        return db_ - da;
      });
    case "due_date":
      return arr.sort((a, b) => {
        const da = a.due_at ? new Date(a.due_at) : new Date("9999");
        const db_ = b.due_at ? new Date(b.due_at) : new Date("9999");
        return da - db_;
      });
    case "assigned_staff":
      return arr.sort((a, b) =>
        (a.assigned_to || "zz").localeCompare(b.assigned_to || "zz")
      );
    default: // oldest_first
      return arr.sort((a, b) => {
        const da = a.created_at ? new Date(a.created_at) : new Date(0);
        const db_ = b.created_at ? new Date(b.created_at) : new Date(0);
        return da - db_;
      });
  }
}

function applyFilters(items, filters) {
  let out = items;
  if (filters.staff)  out = out.filter(i => i.assigned_to === filters.staff);
  if (filters.team)   out = out.filter(i => i.team_label  === filters.team);
  if (filters.type)   out = out.filter(i => i.source_type === filters.type);
  if (filters.aging === "overdue") out = out.filter(i => i.is_overdue);
  if (filters.aging === "old")     out = out.filter(i => {
    const d = daysSince(i.created_at);
    return d !== null && d > 14;
  });
  return out;
}

// ─── Compact Queue Card ───────────────────────────────────────────────────────
/**
 * Two-line compact card (~65-75px on desktop).
 * Primary: Client Name
 * Secondary: Task · Staff · Team · Age
 * Right: Status badge + action button
 */
function CompactQueueCard({ item, tabColor, onItemClick }) {
  const Icon = SRC_ICON[item.source_type] || Activity;
  const age  = daysSince(item.created_at);
  const ageStr = formatAge(age);
  const dueFmt = formatDate(item.due_at);
  const overdue = item.is_overdue;

  const secondaryParts = [];
  if (item.task_title)  secondaryParts.push(item.task_title);
  if (item.context)     secondaryParts.push(item.context);

  const metaParts = [];
  if (item.assigned_to)  metaParts.push(item.assigned_to);
  if (item.team_label)   metaParts.push(item.team_label);
  if (dueFmt && !overdue) metaParts.push(`Due ${dueFmt}`);
  if (overdue)            metaParts.push("OVERDUE");
  else if (ageStr)        metaParts.push(ageStr);

  return (
    <div
      className="flex items-center gap-3 bg-white rounded-xl border border-slate-200 px-4 py-3 hover:shadow-md hover:border-slate-300 transition-all group"
      style={{ borderLeftWidth: "3px", borderLeftColor: tabColor, minHeight: 64 }}
      data-testid={`queue-card-${item.uid}`}
    >
      {/* Icon */}
      <span
        className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center"
        style={{ background: tabColor + "18", color: tabColor }}
      >
        <Icon className="h-3.5 w-3.5" />
      </span>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Row 1: Client name */}
        <div className="font-semibold text-sm leading-tight truncate" style={{ color: BRAND_TEXT }}>
          {item.client_name}
        </div>
        {/* Row 2: Task · context */}
        {secondaryParts.length > 0 && (
          <div className="text-xs text-slate-500 mt-0.5 truncate">
            {secondaryParts.join(" · ")}
          </div>
        )}
        {/* Row 3: Staff · team · age (only when content exists) */}
        {metaParts.length > 0 && (
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            {item.assigned_to && (
              <span className="inline-flex items-center gap-1 text-[10px] text-slate-400">
                <User className="h-2.5 w-2.5 flex-shrink-0" />
                {item.assigned_to}
              </span>
            )}
            {item.team_label && (
              <span className="text-[10px] text-slate-400 border-l border-slate-200 pl-1.5">
                {item.team_label}
              </span>
            )}
            {!item.assigned_to && !item.team_label && (
              <span className="text-[10px] text-slate-300">Unassigned</span>
            )}
            {(overdue || dueFmt || ageStr) && (
              <span
                className={cn(
                  "text-[10px] border-l border-slate-200 pl-1.5",
                  overdue ? "text-red-500 font-semibold" : "text-slate-400"
                )}
              >
                {overdue ? "Overdue" : dueFmt ? `Due ${dueFmt}` : ageStr}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Action button */}
      <button
        onClick={(e) => { e.stopPropagation(); onItemClick(item); }}
        data-testid={`queue-action-${item.uid}`}
        className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg border transition-colors focus:outline-none focus:ring-2"
        style={{
          color: tabColor,
          borderColor: tabColor + "40",
          background: tabColor + "08",
          "--tw-ring-color": tabColor,
        }}
      >
        <ExternalLink className="h-3 w-3" />
        {item.action_label || "Open Task"}
      </button>
    </div>
  );
}

// ─── Sort / filter bar ────────────────────────────────────────────────────────
function SortFilterBar({ sort, setSort, filters, setFilters, items, tabColor }) {
  // Derive available staff and team options from visible items
  const staffOptions = useMemo(() =>
    [...new Set(items.map(i => i.assigned_to).filter(Boolean))].sort(),
    [items]
  );
  const teamOptions  = useMemo(() =>
    [...new Set(items.map(i => i.team_label).filter(Boolean))].sort(),
    [items]
  );
  const typeOptions  = useMemo(() =>
    [...new Set(items.map(i => i.source_type).filter(Boolean))].sort(),
    [items]
  );

  const hasActive = filters.staff || filters.team || filters.type || filters.aging;

  return (
    <div className="flex flex-wrap items-center gap-2 mb-3 pb-3 border-b border-slate-100" data-testid="ops-sort-filter-bar">
      {/* Sort */}
      <div className="relative inline-flex items-center" data-testid="ops-sort-select-wrapper">
        <ArrowUpDown className="absolute left-2.5 h-3 w-3 text-slate-400 pointer-events-none" />
        <select
          value={sort}
          onChange={e => setSort(e.target.value)}
          data-testid="ops-sort-select"
          className="pl-7 pr-7 py-1.5 text-xs border border-slate-200 rounded-lg bg-white text-slate-600 appearance-none focus:outline-none focus:ring-1 cursor-pointer"
          style={{ "--tw-ring-color": tabColor }}
        >
          {SORT_OPTIONS.map(o => (
            <option key={o.key} value={o.key}>{o.label}</option>
          ))}
        </select>
        <ChevronDown className="absolute right-2 h-3 w-3 text-slate-400 pointer-events-none" />
      </div>

      {/* Staff filter */}
      {staffOptions.length > 0 && (
        <div className="relative inline-flex items-center">
          <User className="absolute left-2.5 h-3 w-3 text-slate-400 pointer-events-none" />
          <select
            value={filters.staff || ""}
            onChange={e => setFilters(f => ({ ...f, staff: e.target.value || "" }))}
            data-testid="ops-filter-staff"
            className="pl-7 pr-7 py-1.5 text-xs border border-slate-200 rounded-lg bg-white text-slate-600 appearance-none focus:outline-none focus:ring-1 cursor-pointer"
          >
            <option value="">All Staff</option>
            {staffOptions.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <ChevronDown className="absolute right-2 h-3 w-3 text-slate-400 pointer-events-none" />
        </div>
      )}

      {/* Team filter */}
      {teamOptions.length > 0 && (
        <div className="relative inline-flex items-center">
          <Users className="absolute left-2.5 h-3 w-3 text-slate-400 pointer-events-none" />
          <select
            value={filters.team || ""}
            onChange={e => setFilters(f => ({ ...f, team: e.target.value || "" }))}
            data-testid="ops-filter-team"
            className="pl-7 pr-7 py-1.5 text-xs border border-slate-200 rounded-lg bg-white text-slate-600 appearance-none focus:outline-none focus:ring-1 cursor-pointer"
          >
            <option value="">All Teams</option>
            {teamOptions.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <ChevronDown className="absolute right-2 h-3 w-3 text-slate-400 pointer-events-none" />
        </div>
      )}

      {/* Type filter */}
      {typeOptions.length > 1 && (
        <div className="relative inline-flex items-center">
          <Activity className="absolute left-2.5 h-3 w-3 text-slate-400 pointer-events-none" />
          <select
            value={filters.type || ""}
            onChange={e => setFilters(f => ({ ...f, type: e.target.value || "" }))}
            data-testid="ops-filter-type"
            className="pl-7 pr-7 py-1.5 text-xs border border-slate-200 rounded-lg bg-white text-slate-600 appearance-none focus:outline-none focus:ring-1 cursor-pointer"
          >
            <option value="">All Types</option>
            {typeOptions.map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </select>
          <ChevronDown className="absolute right-2 h-3 w-3 text-slate-400 pointer-events-none" />
        </div>
      )}

      {/* Aging filter */}
      <div className="relative inline-flex items-center">
        <Clock className="absolute left-2.5 h-3 w-3 text-slate-400 pointer-events-none" />
        <select
          value={filters.aging || ""}
          onChange={e => setFilters(f => ({ ...f, aging: e.target.value || "" }))}
          data-testid="ops-filter-aging"
          className="pl-7 pr-7 py-1.5 text-xs border border-slate-200 rounded-lg bg-white text-slate-600 appearance-none focus:outline-none focus:ring-1 cursor-pointer"
        >
          <option value="">All ages</option>
          <option value="overdue">Overdue only</option>
          <option value="old">Older than 14 days</option>
        </select>
        <ChevronDown className="absolute right-2 h-3 w-3 text-slate-400 pointer-events-none" />
      </div>

      {/* Clear all filters */}
      {hasActive && (
        <button
          onClick={() => setFilters({ staff: "", team: "", type: "", aging: "" })}
          data-testid="ops-filter-clear"
          className="text-xs text-slate-400 hover:text-slate-700 underline"
        >
          Clear filters
        </button>
      )}
    </div>
  );
}

// ─── Estimate Kanban card — enhanced with attention reason, next action, CTA ──
/**
 * Four-row card for the Estimates & Follow-ups Kanban.
 * Row 1: Client name
 * Row 2: Proposal number · services summary
 * Row 3: Issue — attention reason (1–2 lines)
 * Row 4: Next action + "Open Workspace →" CTA
 *
 * Navigation: Opens Client/Lead Workspace → Estimates tab.
 * Falls back to /sales/estimates/{id} when client_id is unavailable.
 */
function EstimateKanbanCard({ item, stageColor, onCardClick, prefix }) {
  const isClient = item.responsibility === "CLIENT";
  return (
    <button
      onClick={() => onCardClick(item)}
      data-testid={`${prefix}-card-${item.id}`}
      className="w-full text-left bg-white rounded-xl border border-slate-200 p-3 hover:shadow-md hover:border-slate-300 transition-all focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-300"
      style={{ borderLeftWidth: "3px", borderLeftColor: stageColor }}
    >
      {/* Row 1: Client name */}
      <div
        className="font-semibold text-sm leading-snug truncate"
        style={{ color: BRAND_TEXT }}
        data-testid={`${prefix}-card-client-${item.id}`}
      >
        {item.client_name || item.title || "—"}
      </div>

      {/* Row 2: Proposal number + services */}
      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
        {item.proposal_number && (
          <span
            className="text-[10px] text-slate-400 font-mono"
            data-testid={`${prefix}-card-ref-${item.id}`}
          >
            {item.proposal_number}
          </span>
        )}
        {item.services_summary && (
          <span className="text-[10px] text-slate-400 truncate max-w-[140px]">
            {item.services_summary}
          </span>
        )}
        {item.responsibility && (
          <span
            className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full ml-auto flex-shrink-0"
            style={{
              background: isClient ? "#0D948818" : stageColor + "18",
              color:       isClient ? "#0D9488"   : stageColor,
            }}
            data-testid={`${prefix}-card-responsibility-${item.id}`}
          >
            {isClient ? "Client" : "Staff"}
          </span>
        )}
      </div>

      {/* Row 3: Attention reason */}
      {item.attention_reason && (
        <div
          className="mt-2 text-[11px] leading-snug"
          style={{ color: BRAND_MUTED }}
          data-testid={`${prefix}-card-reason-${item.id}`}
        >
          <span className="font-semibold text-slate-600">Issue: </span>
          {item.attention_reason}
        </div>
      )}

      {/* Row 4: Next action */}
      {item.next_action && (
        <div
          className="mt-1 text-[11px] leading-snug"
          style={{ color: BRAND_MUTED }}
          data-testid={`${prefix}-card-action-${item.id}`}
        >
          <span className="font-semibold text-slate-600">Next: </span>
          {item.next_action}
        </div>
      )}

      {/* Age */}
      {item.stage_since && (
        <div className="text-[10px] text-slate-400 mt-1">{formatAge(daysSince(item.stage_since))}</div>
      )}

      {/* CTA */}
      <div
        className="inline-flex items-center gap-1 mt-2.5 text-[11px] font-semibold"
        style={{ color: stageColor }}
        data-testid={`${prefix}-card-cta-${item.id}`}
      >
        Open Workspace
        <ExternalLink className="h-3 w-3" />
      </div>
    </button>
  );
}

// ─── Kanban card (generic, used by Open Engagements) ─────────────────────────
function KanbanCard({ item, stageColor, onCardClick, prefix }) {
  return (
    <button
      onClick={() => onCardClick(item)}
      data-testid={`${prefix}-card-${item.id}`}
      className="w-full text-left bg-white rounded-xl border border-slate-200 p-3 hover:shadow-md hover:border-slate-300 transition-all focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-300"
      style={{ borderLeftWidth: "3px", borderLeftColor: stageColor }}
    >
      <div className="font-semibold text-sm leading-snug truncate" style={{ color: BRAND_TEXT }}>
        {item.client_name || item.title || "—"}
      </div>
      <div className="text-xs text-slate-500 mt-0.5 truncate">
        {item.engagement_type || item.template_name || item.services_summary || ""}
      </div>
      {(item.engagement_number || item.proposal_number) && (
        <div className="text-[10px] text-slate-400 mt-1 font-mono">
          {item.engagement_number || item.proposal_number}
        </div>
      )}
      {item.assigned_to && (
        <div className="flex items-center gap-1 mt-1.5 text-[10px] text-slate-400">
          <User className="h-2.5 w-2.5 shrink-0" />
          <span className="truncate">{item.assigned_to}</span>
        </div>
      )}
      {item.stage_since && (
        <div className="text-[10px] text-slate-400 mt-1">{formatAge(daysSince(item.stage_since))}</div>
      )}
    </button>
  );
}

function KanbanColumn({ stage, items, onCardClick, prefix, CardComponent, columnWidth }) {
  const Card = CardComponent || KanbanCard;
  const w = columnWidth || 210;
  return (
    <div className="flex-shrink-0" style={{ minWidth: w, width: w }} data-testid={`kanban-col-${stage.key}`}>
      <div className="flex items-center gap-2 mb-3 px-1">
        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: stage.color }} />
        <span className="font-semibold text-[11px] uppercase tracking-wide truncate flex-1" style={{ color: stage.color }}>
          {stage.label}
        </span>
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold flex-shrink-0">
          {items.length}
        </span>
      </div>
      <div className="space-y-2 overflow-y-auto" style={{ maxHeight: 560, scrollbarWidth: "thin" }}>
        {items.map(item => (
          <Card key={item.id} item={item} stageColor={stage.color} onCardClick={onCardClick} prefix={prefix} />
        ))}
        {items.length === 0 && (
          <div className="text-center py-8 text-slate-400 text-xs bg-slate-50 rounded-xl border border-dashed border-slate-200">Clear</div>
        )}
      </div>
    </div>
  );
}

function KanbanBoard({ stages, items, onCardClick, prefix, CardComponent, columnWidth }) {
  return (
    <div className="flex gap-4 overflow-x-auto pb-4" data-testid={`${prefix}-kanban`} style={{ scrollbarWidth: "thin" }}>
      {stages.map(s => (
        <KanbanColumn key={s.key} stage={s}
          items={items.filter(i => i.kanban_stage === s.key)}
          onCardClick={onCardClick} prefix={prefix} CardComponent={CardComponent} columnWidth={columnWidth} />
      ))}
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────
function EmptyQueue({ message }) {
  return (
    <div className="text-center py-14 text-slate-400" data-testid="ops-queue-empty">
      <CheckSquare className="h-10 w-10 mx-auto mb-3 text-emerald-400" />
      <p className="text-sm">{message || "All clear — no items."}</p>
    </div>
  );
}

// ─── Summary card ─────────────────────────────────────────────────────────────
function SummaryCard({ count, label, color, onClick, testid, loading }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="bg-white rounded-xl border border-slate-200 p-4 text-left hover:shadow-md transition-all group focus:outline-none focus:ring-2 focus:ring-offset-1"
      style={{ "--tw-ring-color": color }}
    >
      <div className="flex items-center gap-2 mb-2">
        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
        <span className="text-xs font-medium uppercase tracking-wide truncate" style={{ color: BRAND_MUTED }}>{label}</span>
      </div>
      <div className="font-bold text-3xl leading-none" style={{ color }}>
        {loading ? <span className="text-slate-200">—</span> : count === null ? <span className="text-slate-300">—</span> : count}
      </div>
      <div className="text-xs text-slate-400 mt-2 group-hover:underline">View →</div>
    </button>
  );
}

function LoadingPanel() {
  return (
    <div className="flex justify-center items-center py-20" data-testid="ops-loading">
      <Loader2 className="h-7 w-7 animate-spin text-blue-500" />
    </div>
  );
}

// ─── Queue panel (reusable for 4 queue tabs) ──────────────────────────────────
function QueuePanel({ items, tabKey, tabColor, loading, search }) {
  const [sort,    setSort]    = useState("oldest_first");
  const [filters, setFilters] = useState({ staff: "", team: "", type: "", aging: "" });

  const q = search.trim();
  const filtered  = useMemo(() => items.filter(i => matchSearch(i, q)), [items, q]);
  const afterFilt = useMemo(() => applyFilters(filtered, filters), [filtered, filters]);
  const sorted    = useMemo(() => sortItems(afterFilt, sort), [afterFilt, sort]);
  const navigate  = useNavigate();

  const onItemClick = useCallback((item) => {
    const target = item.link;
    if (target) navigate(target);
    else if (item.client_id) navigate(`/client-workspace/${item.client_id}`);
  }, [navigate]);

  if (loading) return <LoadingPanel />;

  const PANEL_TESTID = `tab-panel-${tabKey}`;

  return (
    <div data-testid={PANEL_TESTID}>
      <SortFilterBar sort={sort} setSort={setSort} filters={filters} setFilters={setFilters}
        items={items} tabColor={tabColor} />
      {sorted.length === 0 ? (
        <EmptyQueue message={
          q || Object.values(filters).some(Boolean)
            ? "No items match the current search or filters."
            : `All clear — no ${tabKey.replace("_", " ")} items.`
        } />
      ) : (
        <div className="space-y-2">
          {sorted.map(item => (
            <CompactQueueCard key={item.uid} item={item} tabColor={tabColor} onItemClick={onItemClick} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function OperationsCenterPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMgmt   = MGMT_ROLES.has(user?.role);

  const [activeTab,   setActiveTab]   = useState("engagements");
  const [scope,       setScope]       = useState("my_work");
  const [search,      setSearch]      = useState("");
  const [loading,     setLoading]     = useState(true);
  const [engagements, setEngagements] = useState(null);
  const [estimates,   setEstimates]   = useState(null);
  const [summary,     setSummary]     = useState(null);

  // ── Data fetch ───────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const sumParams = scope === "my_work" ? { my_work: true } : {};
      const [engsRes, estsRes, sumRes] = await Promise.all([
        api.get("/notifications/ops-engagements", { params: { scope } }),
        api.get("/notifications/ops-estimates",   { params: { scope } }),
        api.get("/notifications/ops-summary",     { params: sumParams }),
      ]);
      setEngagements(engsRes.data.items || []);
      setEstimates(estsRes.data.items   || []);
      setSummary(sumRes.data);
    } catch {
      toast.error("Could not load Operations Center data");
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Normalized queue items ────────────────────────────────────────────────
  const allMessageItems = useMemo(() => {
    if (!summary) return [];
    const msgs  = (summary.messages?.items || []).map(i => normalizeItem(i, "unanswered_message", "messages"));
    const wchat = (summary.webchat?.items  || []).map(i => normalizeItem(i, "webchat_abandoned",  "messages"));
    return [...msgs, ...wchat];
  }, [summary]);

  const waitingStaffItems = useMemo(() => {
    if (!summary) return [];
    const push = (type, src) =>
      (src?.items || []).map(i => normalizeItem(i, type, "waiting_staff"));

    return [
      ...push("production_ready",   summary.production_ready),
      ...push("fbar_ready",         summary.fbar_ready),
      ...push("pending_activation", summary.pending_activation),
      ...push("assigned_task",      summary.assigned_tasks),
      ...push("portal_activated",   summary.portal_activations),
      ...push("follow_up_due",      summary.follow_up_due),
      ...(summary.str_active?.items || [])
          .filter(i => i.responsibility !== "WAITING_ON_CLIENT")
          .map(i => normalizeItem(i, "str_active", "waiting_staff")),
      ...(summary.lifecycle_items?.items || [])
          .filter(i => i.responsibility === "STAFF")
          .map(i => normalizeItem(i, "lifecycle_item", "waiting_staff")),
    ];
  }, [summary]);

  const waitingClientItems = useMemo(() => {
    if (!summary) return [];
    return [
      ...(summary.outstanding_invoices?.items || [])
          .map(i => normalizeItem(i, "outstanding_invoice", "waiting_client")),
      ...(summary.str_active?.items || [])
          .filter(i => i.responsibility === "WAITING_ON_CLIENT")
          .map(i => normalizeItem(i, "str_active", "waiting_client")),
      ...(summary.lifecycle_items?.items || [])
          .filter(i => i.responsibility === "WAITING_ON_CLIENT")
          .map(i => normalizeItem(i, "lifecycle_item", "waiting_client")),
    ];
  }, [summary]);

  const meetingItems = useMemo(() => {
    if (!summary) return [];
    return (summary.zoom?.items || []).map(i => normalizeItem(i, "zoom_review", "meetings"));
  }, [summary]);

  // ── Counts ────────────────────────────────────────────────────────────────
  const messagesCount = summary
    ? (summary.messages?.count ?? 0) + (summary.webchat?.count ?? 0)
    : null;
  const counts = {
    engagements:    engagements?.length  ?? null,
    estimates:      estimates?.length    ?? null,
    messages:       messagesCount,
    waiting_staff:  summary ? waitingStaffItems.length  : null,
    waiting_client: summary ? waitingClientItems.length : null,
    meetings:       summary?.zoom?.count ?? null,
  };

  // ── Filtered Kanban items ─────────────────────────────────────────────────
  const q = search.trim();
  const filteredEngagements = (engagements || []).filter(i => matchSearch(i, q));
  const filteredEstimates   = (estimates   || []).filter(i => matchSearch(i, q));

  // ── Navigation ────────────────────────────────────────────────────────────
  const goEngagement = useCallback((item) => {
    if (item.client_id) navigate(`/client-workspace/${item.client_id}`);
  }, [navigate]);

  const goEstimate = useCallback((item) => {
    // Navigate to Client/Lead Workspace → Estimates tab (canonical deep link).
    // Falls back to direct proposal route when client_id is unavailable.
    if (item.client_id) {
      navigate(`/client-workspace/${item.client_id}?tab=estimates`);
    } else {
      navigate(`/sales/estimates/${item.id}`);
    }
  }, [navigate]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen" style={{ background: BRAND_BG }}>
      <OpsWorkflowNav />
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 pb-12">

        {/* Header */}
        <div className="pt-6 pb-5 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-heading text-2xl font-bold leading-tight" style={{ color: BRAND_TEXT }} data-testid="ops-page-title">
              Operations Center
            </h1>
            <p className="text-sm mt-1" style={{ color: BRAND_MUTED }}>Daily staff command center — six operational views</p>
          </div>
          <button onClick={loadData} disabled={loading} data-testid="ops-refresh-btn"
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg border border-slate-200 bg-white text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors shadow-sm">
            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
            Refresh
          </button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="ops-summary-cards">
          <SummaryCard count={counts.engagements}   label="Open Engagements"    color="#2563EB" onClick={() => setActiveTab("engagements")}   testid="summary-card-engagements"   loading={loading && !engagements} />
          <SummaryCard count={counts.estimates}     label="Pending Estimates"   color="#7C3AED" onClick={() => setActiveTab("estimates")}     testid="summary-card-estimates"     loading={loading && !estimates}   />
          <SummaryCard count={counts.messages}      label="Unanswered Messages" color="#0891B2" onClick={() => setActiveTab("messages")}      testid="summary-card-messages"      loading={loading && !summary}     />
          <SummaryCard count={counts.waiting_staff} label="Waiting on Staff"    color="#D97706" onClick={() => setActiveTab("waiting_staff")} testid="summary-card-waiting-staff" loading={loading && !summary}     />
        </div>

        {/* Controls bar */}
        <div className="flex flex-wrap items-center gap-2.5 mb-4" data-testid="ops-controls-bar">
          {isMgmt && (
            <div className="flex bg-white rounded-lg border border-slate-200 p-0.5" data-testid="ops-scope-selector">
              {[
                { key: "my_work",   label: "My Work"   },
                { key: "my_team",   label: "My Team"   },
                { key: "all_staff", label: "All Staff" },
              ].map(s => (
                <button key={s.key} onClick={() => setScope(s.key)} data-testid={`ops-scope-${s.key}`}
                  className={cn("px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                    scope === s.key ? "text-white shadow-sm" : "text-slate-500 hover:text-slate-800")}
                  style={scope === s.key ? { background: BRAND_NAVY } : {}}>
                  {s.label}
                </button>
              ))}
            </div>
          )}
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search clients, tasks…"
              data-testid="ops-search-input"
              className="w-full border border-slate-200 rounded-lg bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 shadow-sm" />
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex border-b border-slate-200 bg-white rounded-t-xl overflow-x-auto shadow-sm"
          data-testid="ops-tab-bar" style={{ scrollbarWidth: "none" }}>
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            const cnt = counts[tab.key];
            return (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)} data-testid={`ops-tab-${tab.key}`}
                className={cn("relative flex items-center gap-2 px-4 py-3.5 text-sm font-medium whitespace-nowrap transition-colors flex-shrink-0 focus:outline-none",
                  isActive ? "font-semibold" : "text-slate-500 hover:text-slate-800")}
                style={{ color: isActive ? tab.color : undefined }}>
                <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                {tab.label}
                {cnt !== null && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold leading-none"
                    style={{ background: isActive ? tab.color : "#F1F5F9", color: isActive ? "#fff" : "#64748B" }}
                    data-testid={`ops-tab-count-${tab.key}`}>
                    {cnt}
                  </span>
                )}
                {isActive && (
                  <span className="absolute bottom-0 left-0 right-0 h-[2.5px] rounded-t-full"
                    style={{ background: tab.color }} data-testid={`ops-tab-indicator-${tab.key}`} />
                )}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div className="bg-white rounded-b-xl border border-t-0 border-slate-200 p-4 sm:p-6 shadow-sm" data-testid="ops-tab-content">

          {/* Open Engagements */}
          {activeTab === "engagements" && (
            <div data-testid="tab-panel-engagements">
              {engagements === null ? <LoadingPanel /> :
               filteredEngagements.length === 0 && q ? <EmptyQueue message="No engagements match your search." /> :
               engagements.length === 0 ? <EmptyQueue message="No open engagements — all clear." /> :
               <KanbanBoard stages={ENG_STAGES} items={filteredEngagements} onCardClick={goEngagement} prefix="eng" />}
            </div>
          )}

          {/* Estimates */}
          {activeTab === "estimates" && (
            <div data-testid="tab-panel-estimates">
              {estimates === null ? <LoadingPanel /> :
               filteredEstimates.length === 0 && q ? <EmptyQueue message="No estimates match your search." /> :
               estimates.length === 0 ? <EmptyQueue message="No open estimates — all clear." /> :
               <KanbanBoard stages={EST_STAGES} items={filteredEstimates} onCardClick={goEstimate} prefix="est" CardComponent={EstimateKanbanCard} columnWidth={240} />}
            </div>
          )}

          {/* Unanswered Messages */}
          {activeTab === "messages" && (
            <QueuePanel items={allMessageItems} tabKey="messages"
              tabColor="#0891B2" loading={!summary && loading} search={search} />
          )}

          {/* Waiting on Staff */}
          {activeTab === "waiting_staff" && (
            <QueuePanel items={waitingStaffItems} tabKey="waiting_staff"
              tabColor="#D97706" loading={!summary && loading} search={search} />
          )}

          {/* Waiting on Client */}
          {activeTab === "waiting_client" && (
            <QueuePanel items={waitingClientItems} tabKey="waiting_client"
              tabColor="#0D9488" loading={!summary && loading} search={search} />
          )}

          {/* Meetings to Review */}
          {activeTab === "meetings" && (
            <QueuePanel items={meetingItems} tabKey="meetings"
              tabColor="#4F46E5" loading={!summary && loading} search={search} />
          )}

        </div>
      </div>
    </div>
  );
}
